<script setup lang="ts">
import { IconSearch } from '@caks/uikit/icon'
import { InputField } from '@caks/uikit/input'
import { CommonSearchedItem } from '../common'
import { getSearchResults } from '@caks/shared/api/user.route'
import { FoodItem } from '@caks/shared/types/types.api'

interface Emit {
  (emit: 'close'): void
}

const emit = defineEmits<Emit>()

const search = ref('')
const products = ref<FoodItem[]>([])
let timeout: ReturnType<typeof setTimeout>

onMounted(async () => {
  const input = document.getElementById('input')
  input?.focus()

  const { data } = await getSearchResults({
    search: search.value,
    count: 10,
    offset: products.value.length,
  })

  products.value = data

  isLoading.value = false
})

const isLoading = ref(true)

const isRecommendationTitleVisible = computed(() => {
  return search.value.trim() === '' && !isLoading.value
})

const onInput = async (value: string) => {
  search.value = value
  clearTimeout(timeout)

  isLoading.value = true
  products.value = []
  timeout = setTimeout(loadProducts, 500)
}

const loadProducts = async () => {
  const { data } = await getSearchResults({
    search: search.value,
    count: 25,
    offset: products.value.length,
  })

  products.value = data

  isLoading.value = false
}

const searchElement = ref<HTMLElement>()
const onMouseDown = () => {
  searchElement.value?.addEventListener('mouseup', () => {
    emit('close')
  })
}
</script>

<template>
  <div class="search" ref="searchElement" @mousedown="onMouseDown">
    <div class="search__bar">
      <div class="search__wrapper" @mousedown.stop>
        <input-field
          :model-value="search"
          class="header__search-field"
          placeholder="Искать в САК’С"
          :start-icon="IconSearch"
          :no-icons="false"
          id="input"
          @update:model-value="onInput"
        />
        <span v-if="isLoading" class="loader"></span>
        <ul class="search__items">
          <p v-if="isRecommendationTitleVisible">Рекомендации для вас</p>
          <p v-if="!products.length && !isLoading">Ничего не найдено</p>
          <li v-for="item in products">
            <common-searched-item :item="item" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.search {
  display: flex;
  justify-content: center;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: #0000007a;

  &__bar {
    position: relative;
    max-width: 1336px;
    width: 100%;
    height: 100vh;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    position: absolute;
    left: 231px;
    top: 0;
    padding: 16px;
    border-radius: 20px;
    width: 50%;
    max-width: 559px;
    max-height: 759px;
    height: fit-content;
    background-color: $white;
    overflow: hidden;
  }

  &__items {
    overflow-y: scroll;
    max-height: 660px;
    margin-top: 24px;

    p {
      @include h4-8;
      color: $primary-text;
      margin-bottom: 8px;
    }

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: $ui-gray-2;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $sub-text;
      border-radius: 10px;
      border: 2px solid $ui-gray-2;
    }
  }
}

.loader {
  width: 24px;
  min-height: 24px;
  border: 3px solid $primary-green;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
  margin: 24px auto 16px;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
